import clsx from 'clsx';
import { forwardRef, useContext, type ReactNode } from 'react';

import { COMPONENT_NAME } from './terminology-overlay-constants.js';
import { trigger } from './TerminologyOverlay.sty.js';
import { TerminologyOverlayContext } from './TerminologyOverlayContext.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { DOMProps } from '../../core/types/dom.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';

//#region Typings
/**
 * @public
 */
export interface TerminologyOverlayTriggerProps
  extends DOMProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /**
   * Trigger Overlay button must contain children elements.
   */
  children: ReactNode;
}
//#endregion Typings

/**
 * TerminologyOverlay Trigger component, used for rendering trigger content.
 * @public
 */
export const Trigger = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  TerminologyOverlayTriggerProps
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;
  const [behavioralTrackingProps] = useBehavioralTrackingProps(remainingProps);
  const {
    'data-dt-name': dataDtName = 'Trigger',
    ...remainingBehavioralTrackingProps
  } = behavioralTrackingProps;
  const { focusProps, focusClassName, isFocusVisible } = useFocusRing({
    isMinimal: true,
  });
  const overlayTriggerProps = useContext(TerminologyOverlayContext);

  return (
    <button
      className={clsx(
        focusClassName,
        trigger,
        {
          'strato-focus-visible': isFocusVisible,
        },
        consumerClassName,
      )}
      style={{
        textDecoration: isFocusVisible ? 'none' : undefined,
        ...consumerStyle,
      }}
      ref={forwardedRef}
      {..._mergeProps(
        overlayTriggerProps,
        focusProps,
        remainingBehavioralTrackingProps,
      )}
      aria-haspopup="dialog"
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      data-dt-name={dataDtName}
      data-dt-component={COMPONENT_NAME}
    >
      {children}
    </button>
  );
});

(Trigger as typeof Trigger & { displayName: string }).displayName =
  'TerminologyOverlay.Trigger';
